import { useMemo } from "react";

import { HistoricalSpendDTO } from "@/pages/scenario-builder/v3/api/getHistoricalSpend";
import {
  filterHistoricalSpendsForValidation,
  roundDownToNearestThousand,
} from "@/pages/scenario-builder/v3/pages/scenario-form/utils/logic";
import { isDefined } from "@/Utils/array-utils";

export interface BudgetCalculationResult {
  budgetAmount: number;
  maxAllowedBudget?: number;
}

export interface UseBudgetCalculationParams {
  historicalSpendBase: number;
  budgetPercentage: number;
  historicalSpendsDTO?: HistoricalSpendDTO[];
  brand?: string | null;
  geography?: string | null;
}

/**
 * Custom hook for calculating budget amounts
 *
 * Calculates budget based on percentage change from historical spend,
 * with a maximum limit of 2x filtered historical spend (rounded down to nearest thousand).
 * Uses the same filtering and rounding logic as ScenarioFormProvider to ensure consistency.
 *
 * @param historicalSpendBase - The base historical spend amount (should be > 0)
 * @param budgetPercentage - The percentage change from historical spend (-95 to +100)
 * @param historicalSpendsDTO - Historical spend data for calculating max allowed budget
 * @param brand - Brand filter for historical spend validation (optional)
 * @param geography - Geography filter for historical spend validation (optional)
 * @returns Object containing calculated budget amount and maximum allowed budget
 */
export const useBudgetCalculation = ({
  historicalSpendBase,
  budgetPercentage,
  historicalSpendsDTO,
  brand,
  geography,
}: UseBudgetCalculationParams): BudgetCalculationResult => {
  return useMemo(() => {
    // Calculate maximum allowed budget using the same logic as ScenarioFormProvider
    const maxAllowedBudget = historicalSpendsDTO
      ? (() => {
          // Filter to match external service validation (single geography + brand scope)
          const filteredSpends = filterHistoricalSpendsForValidation(historicalSpendsDTO, {
            brand,
            geography,
          });

          const totalHistoricalSpend = filteredSpends.reduce(
            (acc, spend) => acc + spend.marketing_spend.spend,
            0
          );

          return roundDownToNearestThousand(totalHistoricalSpend * 2);
        })()
      : undefined;

    // Calculate budget amount based on percentage change
    // Formula: historicalSpend * (100 + percentage) / 100
    // Examples: 0% = 100% of historical, +100% = 200% of historical, -50% = 50% of historical
    const calculatedBudget = Math.floor((historicalSpendBase * (100 + budgetPercentage)) / 100);

    // Apply maximum budget limit if defined
    const budgetAmount = isDefined(maxAllowedBudget)
      ? Math.min(calculatedBudget, maxAllowedBudget)
      : calculatedBudget;

    return {
      budgetAmount,
      maxAllowedBudget,
    };
  }, [historicalSpendBase, budgetPercentage, historicalSpendsDTO, brand, geography]);
};
